import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiPlus, FiEdit2, FiTrash2, FiEye, FiMapPin, FiGrid, FiLayers } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { stateManagementAPI } from '../../services/stateManagementAPI';
import './StateManagement.css';

const States = () => {
  const navigate = useNavigate();
  const [states, setStates] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedState, setSelectedState] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    totalPages: 1,
    totalMatching: 0,
    totalStates: 0,
    totalCommissionaries: 0,
    totalDistricts: 0
  });
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    capital: ''
  });

  const [errors, setErrors] = useState({
  name: '',
  code: '',
  capital: ''
});
  // Fetch states from API
  const fetchStates = async () => {
    try {
      setLoading(true);
      const response = await stateManagementAPI.getStates(currentPage, itemsPerPage, searchTerm);

      if (response.success) {
        setStates(response.data || []);
        setPagination(response.pagination || {
          totalPages: 1,
          totalMatching: 0,
          totalStates: 0,
          totalCommissionaries: 0,
          totalDistricts: 0
        });
      }
    } catch (error) {
      console.error('Error fetching states:', error);
      toast.error('Failed to fetch states');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStates();
  }, [currentPage, searchTerm]);

  const handleStateClick = (state) => {
    navigate(`/commissionaries/${state._id}`, { state: { stateName: state.name } });
  };

  const handleAdd = () => {
    setModalMode('add');
    setFormData({ name: '', code: '', capital: '' });
    setShowModal(true);
  };

  const handleEdit = (e, state) => {
    e.stopPropagation();
    setModalMode('edit');
    setSelectedState(state);
    setFormData({
      name: state.name,
      code: state.code,
      capital: state.capital
    });
    setShowModal(true);
  };

  const handleView = (state) => {
    navigate(`/commissionaries/${state._id}`, { state: { stateName: state.name } });
  };

  const handleDelete = (e, state) => {
    e.stopPropagation();
    setItemToDelete(state);
    setShowDeleteModal(true);
  };

  const validateForm = () => {
  const tempErrors = {};
  let isValid = true;

  // Name Validation
  if (!formData.name.trim()) {
    tempErrors.name = 'State name is required';
    isValid = false;
  } else if (formData.name.trim().length < 2) {
    tempErrors.name = 'State name must be at least 2 characters';
    isValid = false;
  } else if (formData.name.trim().length > 50) {
    tempErrors.name = 'State name cannot exceed 50 characters';
    isValid = false;
  }

  // Code Validation
  if (!formData.code.trim()) {
    tempErrors.code = 'State code is required';
    isValid = false;
  } else if (formData.code.trim().length < 2) {
    tempErrors.code = 'State code must be at least 2 characters';
    isValid = false;
  } else if (formData.code.trim().length > 50) {
    tempErrors.code = 'State code cannot exceed 50 characters';
    isValid = false;
  }

  // Capital Validation
  if (!formData.capital.trim()) {
    tempErrors.capital = 'Capital is required';
    isValid = false;
  } else if (formData.capital.trim().length < 2) {
    tempErrors.capital = 'Capital must be at least 2 characters';
    isValid = false;
  } else if (formData.capital.trim().length > 50) {
    tempErrors.capital = 'Capital cannot exceed 50 characters';
    isValid = false;
  }

  setErrors(tempErrors);
  return isValid;
};
  const confirmDelete = async () => {
    try {
      setLoading(true);
      await stateManagementAPI.deleteState(itemToDelete._id);
      toast.success('State deleted successfully');
      setShowDeleteModal(false);
      setItemToDelete(null);
      fetchStates(); // Refresh the list
    } catch (error) {
      console.error('Error deleting state:', error);
      toast.error('Failed to delete state');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
  e.preventDefault();

  if (!validateForm()) return;

  try {
    setLoading(true);

    if (modalMode === 'add') {
      await stateManagementAPI.createState(formData);
      toast.success('State added successfully');
    } else {
      await stateManagementAPI.updateState(
        selectedState._id,
        formData
      );
      toast.success('State updated successfully');
    }

    setShowModal(false);
    setErrors({});
    fetchStates();

  } catch (error) {
    console.error('Error saving state:', error);
    toast.error(
      `Failed to ${modalMode === 'add' ? 'add' : 'update'} state`
    );
  } finally {
    setLoading(false);
  }
};
  return (
    <div className="state-management">
      <div className="page-header">
        <div className="header-content">
          <h1 className="page-title">
            <FiMapPin className="title-icon" />
            State Management
          </h1>
          <p className="page-subtitle">Manage states, commissionaries, and districts</p>
        </div>
        <button className="add-btn" onClick={handleAdd}>
          <FiPlus /> Add State
        </button>
      </div>

      {/* Dashboard Cards */}
      <div className="dashboard-cards">
        <div className="dashboard-card">
          <div className="card-content">
            <div className="card-info">
              <h3>Total States</h3>
              <div className="card-value">{pagination.totalStates}</div>
            </div>
            <div className="card-icon states">
              <FiMapPin />
            </div>
          </div>
        </div>
        <div className="dashboard-card">
          <div className="card-content">
            <div className="card-info">
              <h3>Total Commissionaries</h3>
              <div className="card-value">{pagination.totalCommissionaries}</div>
            </div>
            <div className="card-icon commissionaries">
              <FiGrid />
            </div>
          </div>
        </div>
        <div className="dashboard-card">
          <div className="card-content">
            <div className="card-info">
              <h3>Total Districts</h3>
              <div className="card-value">{pagination.totalDistricts}</div>
            </div>
            <div className="card-icon districts">
              <FiLayers />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Controls */}
      <div className="controls-section">
        <div className="search-container">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search states..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1); // Reset to first page when searching
            }}
            className="search-input"
          />
        </div>
      </div>

      {/* States Table */}
      <div className="table-container">
        {loading ? (
          <div className="loading-container">
            <p>Loading...</p>
          </div>
        ) : (
          <table className="states-table">
            <thead>
              <tr>
                <th>State Name</th>
                <th>Code</th>
                <th>Capital</th>
                <th>Commissionaries</th>
                <th>Districts</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {states.length > 0 ? states.map((state) => (
                <tr key={state._id} className="table-row">
                  <td
                    className="state-name clickable"
                    onClick={() => handleStateClick(state)}
                  >
                    {state.name[0].toUpperCase() + state.name.slice(1)}
                  </td>
                  <td onClick={() => handleStateClick(state)}>{state.code}</td>
                  <td onClick={() => handleStateClick(state)}>{state.capital}</td>
                  <td onClick={() => handleStateClick(state)}>{state.commissionaryCount || 0}</td>
                  <td onClick={() => handleStateClick(state)}>{state.districtCount || 0}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="action-buttons">
                      <button
                        className="action-btn view"
                        onClick={() => handleView(state)}
                        title="View Details"
                      >
                        <FiEye />
                      </button>
                      <button
                        className="action-btn edit"
                        onClick={(e) => handleEdit(e, state)}
                        title="Edit"
                      >
                        <FiEdit2 />
                      </button>
                      <button
                        className="action-btn delete"
                        onClick={(e) => handleDelete(e, state)}
                        title="Delete"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
                : (
                  <tr>
                    <td colSpan="5" style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                      No state found!
                    </td>
                  </tr>
                )

              }
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="pagination">
        <button
          className="pagination-btn"
          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
          disabled={currentPage === 1 || loading}
        >
          Previous
        </button>
        <span className="pagination-info">
          Page {currentPage} of {pagination.totalPages}
        </span>
        <button
          className="pagination-btn"
          onClick={() => setCurrentPage(prev => Math.min(prev + 1, pagination.totalPages))}
          disabled={currentPage === pagination.totalPages || loading}
        >
          Next
        </button>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {modalMode === 'add' ? 'Add State' : 'Edit State'}
              </h2>
              <button
                className="modal-close"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>State Name</label>
              <input
  type="text"
  value={formData.name}
  onChange={(e) => {
    setFormData({
      ...formData,
      name: e.target.value.slice(0, 50)
    });

    setErrors({
      ...errors,
      name: ''
    });
  }}
/>

{errors.name && (
  <span className="error-text">{errors.name}</span>
)}
              </div>
              <div className="form-group">
                <label>State Code</label>
                <input
  type="text"
  value={formData.code}
  onChange={(e) => {
    setFormData({
      ...formData,
      code: e.target.value.slice(0, 50)
    });

    setErrors({
      ...errors,
      code: ''
    });
  }}
/>

{errors.code && (
  <span className="error-text">{errors.code}</span>
)}
              </div>
              <div className="form-group">
                <label>Capital</label>
             <input
  type="text"
  value={formData.capital}
  onChange={(e) => {
    setFormData({
      ...formData,
      capital: e.target.value.slice(0, 50)
    });

    setErrors({
      ...errors,
      capital: ''
    });
  }}
/>

{errors.capital && (
  <span className="error-text">{errors.capital}</span>
)}
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)} disabled={loading}>
                  Cancel
                </button>
                <button type="submit" className="primary" disabled={loading}>
                  {loading ? 'Saving...' : (modalMode === 'add' ? 'Add State' : 'Update State')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm Delete</h2>
              <button
                className="modal-close"
                onClick={() => setShowDeleteModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete <strong>{itemToDelete?.name}</strong>?</p>
              <p className="warning-text">This action cannot be undone.</p>
            </div>
            <div className="modal-actions">
              <button type="button" onClick={() => setShowDeleteModal(false)} disabled={loading}>
                Cancel
              </button>
              <button type="button" className="danger" onClick={confirmDelete} disabled={loading}>
                {loading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default States;